import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Button, ButtonProps, createStyles, Tooltip} from '@mantine/core';
import {useId} from '@mantine/hooks';
import classNames from 'classnames';
import React from 'react';

import {getDisabledTooltipButtonProps} from 'lib/a11y/ButtonA11y';
import {FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';

interface ButtonWithDisabledReasonProps
  extends
    Omit<ButtonProps, 'disabled'>,
    Omit<React.ComponentPropsWithoutRef<'button'>, keyof ButtonProps | 'disabled' | 'onClick'> {
  /** Disabled state is controlled by `disabledTooltip`, not this prop. */
  disabled?: never;
  dataTestAttrs?: DataTestAttrs;
  /** Why the button is unavailable. `null` leaves the button enabled and renders no tooltip. */
  disabledTooltip: string | null;
  onClick: NonNullable<React.ComponentPropsWithoutRef<'button'>['onClick']>;
}

/**
 * A button that states why it is unavailable. The reason shows as a tooltip on hover or keyboard focus, and
 * is linked to the button through `aria-describedby`, so screen readers announce it rather than only
 * reporting that the button is disabled. Accepts any Mantine `Button` prop.
 */
export default function ButtonWithDisabledReason({
  'aria-describedby': ariaDescribedBy,
  className,
  dataTestAttrs,
  disabledTooltip,
  onClick,
  sx,
  ...buttonProps
}: ButtonWithDisabledReasonProps): JSX.Element {
  const {classes} = useStyles();
  const disabledReasonId = useId();
  const isDisabled = disabledTooltip != null;
  const {sx: disabledSx, ...disabledButtonProps} = getDisabledTooltipButtonProps(isDisabled, onClick, true);
  return (
    <>
      <Tooltip
        events={{focus: true, hover: true, touch: false}}
        label={disabledTooltip ?? ''}
        disabled={!isDisabled}
        multiline={true}
        width={240}
        // Callers include toolbar panels that clip their overflow, which would cut the label off at the edge.
        withinPortal={true}
      >
        <Button
          // ButtonHarness.assertIsDisabled/assertIsEnabled key off this class for both legacy and Mantine
          // buttons; keep it so callers' existing tests don't need to special-case this component.
          className={classNames(className, classes.button, {'is-disabled': isDisabled})}
          {...buttonProps}
          aria-describedby={
            isDisabled
              ? [ariaDescribedBy, disabledReasonId].filter((id) => id != null).join(' ')
              : ariaDescribedBy
          }
          {...disabledButtonProps}
          sx={disabledSx == null ? sx : [sx, disabledSx].flat()}
          {...(dataTestAttrs ? getTestProps(dataTestAttrs) : {})}
        />
      </Tooltip>
      {/* The tooltip only mounts its label while hovered, so the reason lives here too, always mounted, to
          stay reachable from the button's aria-describedby without hovering. */}
      {isDisabled && (
        <span className={classes.visuallyHidden} id={disabledReasonId}>
          {disabledTooltip}
        </span>
      )}
    </>
  );
}

const useStyles = createStyles(() => ({
  button: {
    // Match the regular weight of the legacy `ui-library` buttons still used elsewhere in these panels.
    // `&&` bumps specificity above the theme's own font-weight rule.
    '&&': {
      fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    },
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
}));
